import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { ACCESS_KEY } from '@vindr/services/constants/auth.constant';
import {
  CONFIG_SERVER,
  LANGUAGES,
} from '@vindr/services/constants/common.constant';
import {
  DefaultErrorResponse,
  DefaultResponse,
} from '@vindr/services/interfaces';
import { SelectProps } from 'antd';
import { AxiosError } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { isVietnamese } from '../formatText';

export const getViewportApiKey = () => {
  const viewerLink = '/viewer';
  const url = new URL(window.location.href);

  if (url?.pathname && !url.pathname.includes(viewerLink)) {
    return null;
  }

  return url.searchParams.get(ACCESS_KEY);
};

export const getDateFormatted = (showTime = false) => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const i18n = require('@ohif/i18n');
  const time = showTime ? ' HH:mm:ss' : '';

  return i18n.default.language === LANGUAGES.VI
    ? `DD/MM/YYYY${time}`
    : `MM/DD/YYYY${time}`;
};

/**
 * Group array by key
 *
 * @example groupBy([{name: 'foo', age: 1}, {name: 'bar', age: 2}], 'name') => {foo: [{name: 'foo', age: 1}], bar: [{name: 'bar', age: 2}]}
 */
export function groupBy(list: any[], props: string | number) {
  return list.reduce((a, b) => {
    (a[b[props]] = a[b[props]] || []).push(b);

    return a;
  }, {});
}

/* const checkHospital = (hospital: string) => `${window.origin}`.indexOf(hospital) > -1;

export const isThaiThuyHospital = () => checkHospital('dkthaithuy.vindr.ai');
export const isHungHaHospital = () => checkHospital('bvhungha.vindr.ai'); */

export const getRealmId = () => {
  const realmId = CONFIG_SERVER.REALM_ID;

  if (realmId) return realmId;

  return 'unknown';
};

/* export const getTenant = () => {
  // Get tenant from config
  if (window?.config?.tenant) return window.config.tenant;
  // Get tenant from domain
  const host = process.env.BACKEND_URL || window.location.hostname;

  if (host.indexOf('.vindr.ai') < 0) {
    return 'local';
  } else {
    return host.replace('.vindr.ai', '').replace('https://', '').replace('http://', '');
  }
}; */

/* export const resetExpandLayout = () => {
  try {
    const viewportContainer = document.querySelector('.js-viewport-gird-container');

    if (viewportContainer.classList.contains('expand-container')) {
      viewportContainer.classList.remove('expand-container');

      const allViewportElms = document.querySelectorAll('.js-viewport-grid-item') || [];

      allViewportElms.forEach((elm) => {
        elm.classList.remove('is-absolute-item');
        elm.classList.remove('is-expand-item');
      });
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error(error);
  }
}; */

// export const isDiff = (A: never, B: never) => JSON.stringify(A) !== JSON.stringify(B);

export const uuidv4 = () => {
  if (window.crypto?.randomUUID) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;

    return v.toString(16);
  });
};

export const resizedataURL = (datas, x, y, scale) => {
  return new Promise((resolve) => {
    // We create an image to receive the Data URI
    const img = document.createElement('img');

    // When the event "onload" is triggered we can resize the image.
    img.onload = function () {
      const inputWidth = img.naturalWidth;
      const inputHeight = img.naturalHeight;
      const wantedWidth = inputWidth / scale;
      const wantedHeight = inputHeight / scale;
      // We create a canvas and get its context.
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // We set the dimensions at the wanted size.
      canvas.width = wantedWidth;
      canvas.height = wantedHeight;

      // We resize the image with the canvas method drawImage();
      ctx.drawImage(
        this,
        x,
        y,
        wantedWidth,
        wantedHeight,
        0,
        0,
        wantedWidth,
        wantedHeight
      );

      const dataURI = canvas.toDataURL();

      // This is the return of the Promise
      resolve(dataURI);
    };

    // We put the Data URI in the image's src attribute
    img.src = datas;
  });
}; // Use it like : var newDataURI = await resizedataURL('yourDataURIHere', 50, 50);

/* export const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  const ext = mime.replace('image/', '');

  return new File([u8arr], `${filename}.${ext}`, { type: mime });
};

export const fileToDataURL = (file) => {
  return new Promise((resole) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      resole(e.target.result);
    };
    reader.readAsDataURL(file);
  });
};
 */
export const getFileType = (type: File['type']) => {
  if (['pdf'].includes(type.split('/')[1])) {
    return 'pdf';
  } else if (type.includes('video')) {
    return 'video';
  } else if (['image/jpeg', 'image/png', 'image/gif'].includes(type)) {
    return 'img';
  }

  return 'other';
};

export function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export function localStorageSetWithExpiry(
  key: string,
  value: any,
  expireIn: any
) {
  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value,
    expiry: expireIn,
  };

  localStorage.setItem(key, JSON.stringify(item));

  return item;
}
export function localStorageGetWithExpiry(key) {
  const itemStr = localStorage.getItem(key);

  // if the item doesn't exist, return null
  if (!itemStr) return null;

  const item = JSON.parse(itemStr);
  const now = new Date();

  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    // If the item is expired, delete the item from storage
    // and return null
    localStorage.removeItem(key);

    return null;
  }

  return item.value;
}

export function isMobile(width, height) {
  const MOBILE_SIZE_LIMIT = 600;

  return width < MOBILE_SIZE_LIMIT;
}

/**
 * Condition show object
 * @use { ...conditionObj('key', {key: 'value'}, condition) }
 */
export const conditionObj = (
  key: string | undefined,
  val: any,
  condition?: boolean
): Record<string, unknown> | undefined => {
  if (!key) {
    return condition !== undefined ? condition && val : val;
  }

  if (condition !== undefined) {
    return condition && { [key]: val };
  } else if (typeof val === 'number' || !isEmpty(val)) {
    return { [key]: val };
  } else return {};
};

export const splitURL = (url: string) => {
  if (!url) return '';
  // If support new URL
  if (window.URL || typeof URL === 'function') {
    return new URL(url);
  }
  // If not support new URL
  const urlObj = document.createElement('a');

  urlObj.href = url;

  return urlObj;
};

export const cleanDiacritics = (str: string) => {
  if (!str) return '';
  str = str.trim().toLowerCase();

  str = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  str = str.replace(/[đĐ]/g, 'd');
  str = str.replace(/([^0-9a-z-_.\s])/g, '');
  str = str.replace(/-+/g, '-');

  return str.replace(/^(-+|-+)$/g, '');
};

export const renderAvailable = (...args: unknown[]) => {
  for (const arg of args) if (!isEmpty(arg)) return arg;

  return '-';
};

export const throwError = (
  actual: any,
  expected?: any,
  message?: undefined | string
) => {
  let defaultMessage: string;

  const formatShow = (value) => {
    switch (typeof value) {
      case 'object':
        return Array.isArray(value)
          ? `[${value.join(', ')}]`
          : JSON.stringify(value);
      case 'string':
        return `'${value}'`;
      default:
        return `${value}`;
    }
  };

  switch (typeof expected) {
    case 'object':
      defaultMessage = Array.isArray(expected)
        ? `Expected one of ${formatShow(expected)}`
        : `Expected ${formatShow(expected)}`;
      break;
    case 'string':
      defaultMessage = `Expected '${expected}'`;
      break;
    case 'undefined':
      throw new Error(
        `Invalid actual value: ${actual}, type: ${typeof actual}`
      );
    default:
      defaultMessage = `Expected ${expected}`;
  }
  throw new Error(`Got ${formatShow(actual)}. ${message || defaultMessage}`);
};

/**
 * Searches for occurrences of `searchText` within `dataText`, considering:
 *
 * - Vietnamese characters: If `searchText` contains Vietnamese characters, the original `dataText` is searched directly.
 * - Non-Vietnamese characters: If `searchText` does not contain Vietnamese characters, the function searches for matches in both the original `dataText` and a version of `dataText` with diacritics removed (e.g., "à" becomes "a").
 *
 * @example:
 *  - Searching for "Co" (Not Vietnamese): Matches "Co", "Cô", "Cố", "Cớ", etc...
 *  - Searching for "Cô" (is Vietnamese): Matches only "Cô".
 */
export function handleSearchVietnamese(
  searchText: string,
  dataText: string,
  t: any
) {
  if (
    !['string', 'number'].includes(typeof searchText) ||
    !['string', 'number'].includes(typeof dataText)
  )
    return dataText;

  searchText = String(searchText).trim().toLowerCase();
  dataText = String(dataText);

  const patterns = [searchText, typeof t === 'function' && t(searchText)]
    .filter(Boolean)
    .join('|');

  const searchRegex = new RegExp(`(${patterns}).*`, 'ig');

  return isVietnamese(searchText)
    ? dataText.match(searchRegex)
    : cleanDiacritics(dataText).match(searchRegex);
}

// Delete all invalid value in object
export function filterValidValue<T>(
  obj: T,
  options?: {
    invalidList?: any[];
    invalidKey?: (keyof T)[] | any[];
  }
): T {
  const invalidList = options?.invalidList || [null, undefined];
  const invalidKey = options?.invalidKey || ['page', 'size'];
  if (isEmpty(obj)) return obj;

  return Object.keys(obj).reduce((acc, key) => {
    if (invalidKey.includes(key)) return acc;
    if (invalidList.includes(obj[key])) return acc;
    if (Array.isArray(obj[key])) {
      if (obj[key].length === 0) return acc;

      acc[key] = obj[key];

      return acc;
    }

    if (typeof obj[key] === 'object') {
      acc[key] = filterValidValue(obj[key], { invalidKey, invalidList });

      return acc;
    }

    return {
      ...acc,
      [key]: obj[key],
    };
  }, {} as T);
}

/**
 * Fill object with keys, if key not exist in object, fill with value
 *
 * @example {a: 1, b: 2}, now want to fill with key 'a', 'c'. if key not exist, fill with value
 * fillWithKeys({a: 1, b: 2}, ['a', 'c'], 0) => {a: 1, b: 2, c: 0}
 */
export function fillWithKeys(
  obj: Record<string, unknown>,
  keys: string[],
  value: unknown
) {
  if (!obj) return obj;

  return keys.reduce((acc, key) => {
    acc[key] = obj[key] ? obj[key] : value;

    return acc;
  }, obj);
}

/**
 * Make all value of object is array
 * @example makeArrayAllValues({a: 1, b: 2}) => {a: [1], b: [2]}
 */
export function makeArrayAllValues(obj: Record<string, unknown>) {
  if (!obj) return obj;

  return Object.keys(obj).reduce((acc, key) => {
    acc[key] = Array.isArray(obj[key]) ? obj[key] : [obj[key]];

    return acc;
  }, {});
}

/**
 * Recursively replaces all occurrences of a specified value with another value in an object.
 * @example replaceAllValues({a: 1, b: {c: 2, d: 3, e: [4]}}, 4, 'FOUR') => { a: 1, b: { c: 2, d: 3, e: [ 'FOUR' ] } }
 */

export function replaceAllValues(obj: any, oldValue: any, newValue: any) {
  if (Array.isArray(obj)) {
    return obj.map((element) => replaceAllValues(element, oldValue, newValue));
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = replaceAllValues(obj[key], oldValue, newValue);
      return acc;
    }, {});
  } else {
    return obj === oldValue ? newValue : obj;
  }
}

/**
 * Replace value with key
 * @example: replaceValWithKey({a: 1, b: 2, d: {a: 2}}, ['a', 'c'], 0) => {a: 0, b: 2, d: {a: 0}}
 */
export function replaceValWithKey<T>(
  obj: T,
  keysToReplace: Array<keyof T>,
  replacementValue: any
) {
  // Base case: if the input is not an object, return it as is
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  // Iterate through each key in the object
  for (const key in obj) {
    // Check if the current key is in the keysToReplace array
    if (keysToReplace.includes(key)) {
      // If yes, replace the value with the specified replacementValue
      obj[key] = replacementValue;
    } else {
      // If no, recursively call the function on the nested object (if any)
      obj[key] = replaceValWithKey(obj[key], keysToReplace, replacementValue);
    }
  }

  // Return the modified object
  return obj;
}

/**
 * Convert file size to human readable format
 * Base 10: 1 KB = 1000 Bytes
 * Base 2: 1 KB = 1024 Bytes
 *
 * @example fileSize(1024) => 1 KB
 * @example fileSize(1024, 2) => 1.02 KB
 */
export function fileSize(size: number, base: 1000 | 1024 = 1000) {
  if (size === 0) return '0 Byte';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const indexBySize = Math.floor(Math.log(size) / Math.log(base));

  return `${parseFloat((size / base ** indexBySize).toFixed(2))} ${
    sizes[indexBySize]
  }`;
}

/* export function splitFileName(fileName, length, lengthLastWord = 3) {
  if (!fileName) return '';
  if (fileName.length <= length) return fileName;
  const ext = fileName.split('.').pop();
  const lengthExt = ext.length + 1;

  if (lengthExt >= length)
    return `${fileName.slice(0, length - lengthLastWord - 1)}...${fileName.slice(-lengthLastWord)}`;

  return `${fileName.slice(0, length - lengthExt - 1 - lengthLastWord - 1)}...${fileName.slice(
    -lengthLastWord - lengthExt
  )}`;
} */

export function customSearch(
  input: string,
  labelList: Record<string, string | number>,
  searchVietnamese: boolean,
  t: (arg0: string) => string
) {
  if (searchVietnamese) {
    return Object.keys(labelList).some((key) => {
      return (
        ['string', 'number'].includes(typeof labelList[key]) &&
        Boolean(
          handleSearchVietnamese(String(input), String(labelList[key]), t)
        )
      );
    });
  }

  return Object.keys(labelList).some((key) => {
    return (
      ['string', 'number'].includes(typeof labelList[key]) &&
      String(labelList[key]).toLowerCase().includes(String(input).toLowerCase())
    );
  });
}

/**
 * Replaces all values of a specified key with a given value
 * @example I want to replace all values of key 'a' with null
 * replaceAllValuesForKey([{a: 1, b: 2}, {a: 2, c: 3}], 'a', null) => [{a: null, b: 2}, {a: null, c: 3}]
 */
export function replaceAllValuesForKey(
  list: Array<Record<string, unknown>>,
  key: string,
  value: unknown
) {
  if (!list) return list;

  return list.map((item: Record<string, unknown>) => {
    const newItem: Record<string, unknown> = {};

    Object.keys(item).forEach((itemKey: string) => {
      if (itemKey === key) {
        newItem[itemKey] = value;
      } else if (typeof item[itemKey] === 'object') {
        newItem[itemKey] = replaceAllValuesForKey(
          item[itemKey] as Array<Record<string, unknown>>,
          key,
          value
        );
      } else {
        newItem[itemKey] = item[itemKey];
      }
    });

    return newItem;
  });
}

/**
 * Get unique array
 * @example uniqueArray([1, 1, 2]) => [1, 2]
 */
export function uniqueArray<T>(list: Array<T>) {
  if (!list) return list;

  //? Using Reduce because it's faster than using Set
  return list.reduce((acc: Array<T>, item: T) => {
    if (!acc.some((i) => i === item)) {
      acc.push(item);
    }

    return acc;
  }, []);
}

/**
 * Get unique object in array
 * @example uniqueObjectArray([{a: 1}, {a: 1}, {a: 2}], 'a', 'all') => [{a: 1}, {a: 2}]
 * @example uniqueObjectArray([{a: 1}, {a: 1}, {a: 2}], 'a', 'key') => [1, 2]
 */
export function uniqueObjectArray<T>(
  list: T[],
  key: string,
  type: 'all' | 'key'
): T[] | any[] {
  if (!list) return list;

  const result = list.reduce((acc: T[], item: T) => {
    if (!acc.some((i) => i[key] === item[key])) {
      acc.push(item);
    }

    return acc;
  }, []) as T[];

  switch (type) {
    case 'all':
      return result;
    case 'key':
      return result.map((item) => item[key]);
    default:
      return result;
  }
}

export function uniquePushArray<T extends Record<string, any>>(
  list: T[],
  itemsToAdd: T | T[],
  uniqueKey: keyof T = 'value' as keyof T
): T[] {
  if (!Array.isArray(list) || isEmpty(itemsToAdd))
    return list;

  const items = Array.isArray(itemsToAdd) ? itemsToAdd : [itemsToAdd];

  const existingKeys = new Set(list.map((item) => item[uniqueKey]));

  const newUniqueItems = items.filter((item) => {
    const key = item[uniqueKey];
    if (!existingKeys.has(key)) {
      existingKeys.add(key);
      return true;
    }
    return false;
  });

  return [...newUniqueItems, ...list];
}

/**
 * Check if object is empty
 *
 * @example isEmptyObject({}) => true
 */

export function isEmptyObject<T extends Record<any, any>>(obj: T): boolean {
  return typeof obj === 'object' && Object.keys(obj).length === 0;
}

export function Pick<T>(obj: T, keys: (keyof T)[]) {
  if (!obj) return obj;

  return keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      acc[key] = obj[key];
    }

    return acc;
  }, {} as Partial<T>);
}

export function Omit<T>(obj: T, keys: Array<keyof T>): Partial<T> {
  if (!obj) return obj;

  return Object.keys(obj).reduce((acc, key) => {
    if (!keys.includes(key as keyof T)) {
      (acc as T)[key as keyof T] = obj[key as keyof T];
    }
    return acc;
  }, {} as Partial<T>);
}

type Func<TArgs extends any[], TResult> = (...args: TArgs) => TResult; // Generic function type

export function once<T extends (...args: any[]) => any>(func: T): T {
  let ran = false;
  let result: ReturnType<T>;
  return function (this: any, ...args: Parameters<T>): ReturnType<T> {
    if (ran) return result;
    result = func.apply(this, args);
    ran = true;
    return result;
  } as T;
}

export function sortAlphaText<T>(
  arr: T[],
  type: 'asc' | 'desc' = 'asc',
  keySelector?: (item: T) => string
): T[] {
  if (!arr) return arr;

  return arr.sort((a, b) => {
    const keyA = keySelector ? keySelector(a) : (a as unknown as string);
    const keyB = keySelector ? keySelector(b) : (b as unknown as string);

    return keyA.localeCompare(keyB) * (type === 'asc' ? 1 : -1);
  });
}

export function sortObjectKeys<T extends Record<string, any>>(obj: T): T {
  if (!obj) return obj;

  return sortAlphaText(Object.keys(obj)).reduce((acc, key) => {
    acc[key as keyof T] = obj[key as keyof T];

    return acc;
  }, {} as T);
}

export function sortDates(arr: string[], type?: 'asc' | 'desc' = 'asc') {
  if (!arr) return arr;

  return arr.sort((a, b) => {
    return (
      new Date(a).getTime() - new Date(b).getTime() * (type === 'asc' ? 1 : -1)
    );
  });
}

export function DownloadFile(
  data: BlobPart | never,
  fileName: string,
  type?: string
) {
  const blob = new Blob([data], { type });
  const link = document.createElement('a');

  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();

  //? For Firefox it is necessary to delay revoking the ObjectURL
  setTimeout(() => {
    window.URL.revokeObjectURL(link.href);
  }, 100);

  return link;
}

/**
 * Groups an array of objects by a specific key.
 * @example groupByKey([{id: 1, name: 'a'}, {id: 2, name: 'b'}, {id: 1, name: 'c'}], 'name')
 * => {a: {id: 1, name: 'a'}, b: {id: 2, name: 'b'}, c: {id: 1, name: 'c'}}
 *
 * @param arr - Array of objects to group.
 * @param key - Key to group the objects by.
 * @returns A record (object) with keys as the values of the provided key in the array elements, and values as the corresponding objects.
 */
export function groupByKey<T extends Record<string, any>>(
  arr: T[],
  key: keyof T
): Record<string, T> {
  if (!Array.isArray(arr)) {
    console.error('Input not array type: ', arr);
    return;
  }

  return arr.reduce((acc, item) => {
    const groupKey = String(item[key]);
    acc[groupKey] = item;
    return acc;
  }, {} as Record<string, T>);
}

export function generateRowKey(arr: unknown[]) {
  return arr.map((item) => String(item)).join('|');
}

export function getArrayVal<k>(val: k[] | k, index = 0): k {
  return Array.isArray(val) ? val[index] : val;
}

export function stopReload(event: BeforeUnloadEvent) {
  event.preventDefault();
  return (event.returnValue = '');
}

export function disabledEventPropagation(e) {
  if (e) {
    if (e.stopPropagation) {
      e.stopPropagation();
    } else if (window.event) {
      window.event.cancelBubble = true;
    }
  }
}

export function randomVal(arr: any[]) {
  if (!Array.isArray(arr) || arr?.length === 0) {
    return arr;
  }

  const randomIndex = Math.floor(Math.random() * arr.length);

  return arr[randomIndex];
}

export function defaultGetNextPageParam<T, U>(
  lastPage: T | any,
  allPages: U | any
) {
  if (isEmpty(allPages)) return;

  const maxOffset = lastPage.count - 1;
  const currentOffset = allPages.reduce(
    (acc, page) => acc + page.data?.length,
    0
  );
  return currentOffset < maxOffset ? currentOffset : undefined;
}

export async function handlePopupScroll(
  e: React.UIEvent<HTMLElement>,
  hasNextPage: boolean,
  fetchNextPage: UseInfiniteQueryResult['fetchNextPage'],
  isFetching: boolean,
  threshold = 300
) {
  const target = e.target as HTMLElement;
  if (!hasNextPage || !target) return;
  const { scrollHeight, scrollTop, clientHeight } = target;

  // if (scrollTop + offsetHeight === scrollHeight) {
  if (!isFetching && scrollHeight - scrollTop - clientHeight < threshold) {
    return await fetchNextPage();
  }
}

export function addToArray<T, D>(val: T | T[], initial?: D): T[] | D {
  return Array.isArray(val) ? val : isEmpty(val) ? initial : [val];
}

export function flatPages<T extends DefaultResponse>(
  arr: InfiniteData<T>
): T['data'] {
  return arr?.pages
    ?.flat()
    ?.map((x) => x?.data)
    ?.flat();
}

/**
 * @example: <Select filterOption={filterBothValueAndKey} />
 */
export const filterBothValueAndKey: SelectProps['filterOption'] = (
  input,
  option
) => {
  return (
    String(option.children).toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
    option.value.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
    option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
  );
};

export function floorDecimals(value: number, decimals: number): number {
  return Number(`${Math.floor(value * 10 ** decimals) / 10 ** decimals}`);
}

export function deepTrim(input: string | Array<any> | Record<string, any>) {
  if (typeof input === 'string') {
    return input.trim();
  } else if (Array.isArray(input)) {
    return input.map((item) => deepTrim(item));
  } else if (typeof input === 'object' && input !== null) {
    const trimmedObject = {};
    for (const key in input) {
      if ({}.propertyIsEnumerable.call(input, key)) {
        trimmedObject[key] = deepTrim(input[key]);
      }
    }
    return trimmedObject;
  } else {
    return input;
  }
}

/**
 * @example: enum E { foo, bar }, key = ['foo', 'bar', 0, 1]; enumKeys(E) = ['foo', 'bar']
 */
export function enumKeys<
  T extends Record<string, unknown>,
  Keys extends keyof T
>(obj: T): Keys[] {
  return Object.keys(obj).filter((k) => isNaN(Number(k))) as Keys[];
}

export function debounce(func: Function, wait: number, immediate: boolean) {
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export function isValidTimeFormat(format: string): boolean {
  // Regular expression to match common time formats
  const timeFormatRegex = /^(HH:mm(:ss)?|hh:mm(:ss)?\s?(a|A)?)$/;

  return timeFormatRegex.test(format);
}

/**
 * @description: set src image to base64, bypass cross-origin
 * @example: <img src={await setSrcImage('https://...')} />
 *
 * @param {string} src
 * @return {Promise<string>}  base64 string
 */
export async function getImageSrc(url: string): Promise<string> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error(`setSrcImage: `, error);
    return;
  }
}

export async function handleBatchOperation<T>({
  promises,
  message,
  t,
  successMessage,
  errorMessage,
}: {
  promises: Promise<T>[];
  message: any;
  t: (val: string) => string;
  successMessage?: (index: number) => string;
  errorMessage?: (error: AxiosError<DefaultErrorResponse>) => string;
}) {
  try {
    const responses = await Promise.allSettled(promises);

    responses.forEach((result, index) => {
      if (
        result.status === 'fulfilled' &&
        typeof successMessage === 'function'
      ) {
        message.success({ content: successMessage(index) });
      } else if (
        result.status === 'rejected' &&
        typeof errorMessage === 'function'
      ) {
        message.error({ content: errorMessage(result.reason) });
      }
    });
  } catch (error) {
    const errMess = 'An unexpected error occurred.';
    message.error({ content: t ? t(errMess) : errMess });
  }
}
